import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import { LucideBot, LucideFileText } from "lucide-react"
import { useState, useEffect } from "react"
import { type PromptConfig } from "@/api/types/config"
import { LLMServiceType, LLM_COSTS, LLM_DISPLAY_NAMES } from "@/types/services"
import type { ConfigComponentProps } from '../ConfigForm'

const DEFAULT_PROMPT_CONFIG: PromptConfig = {
  prompt_text: '',
  json_schema: {},
  service_type: 'LLM_SONNET35' as const
}

export function PromptSettings({ config, onUpdate, isLoading }: ConfigComponentProps) {
  const [promptConfig, setPromptConfig] = useState<Record<string, PromptConfig>>(
    config?.prompt_config || {
      legislative_review: { ...DEFAULT_PROMPT_CONFIG },
      monitor: { ...DEFAULT_PROMPT_CONFIG }
    }
  )
  const [showPromptWarning, setShowPromptWarning] = useState(false)

  // Update parent when local state changes
  useEffect(() => {
    onUpdate({
      prompt_config: promptConfig
    })
  }, [promptConfig, onUpdate])

  // Update local state when config changes (workspace switch)
  useEffect(() => {
    setPromptConfig(config?.prompt_config || {
      legislative_review: { ...DEFAULT_PROMPT_CONFIG },
      monitor: { ...DEFAULT_PROMPT_CONFIG }
    })
  }, [config?.prompt_config])

  const handlePromptConfigChange = (key: string, updates: Partial<PromptConfig>) => {
    if (!showPromptWarning) setShowPromptWarning(true)
    setPromptConfig(prev => ({
      ...prev,
      [key]: { ...prev[key], ...updates }
    }))
  }

  const formatCost = (llmType: LLMServiceType): string => {
    const cost = LLM_COSTS[llmType]
    return `$${cost.inputCost}/1K input, $${cost.outputCost}/1K output`
  }

  const renderPromptConfig = (key: string, config: PromptConfig) => {
    const displayName = key === 'legislative_review' ? 'Legislative Review' : 
                       key === 'monitor' ? 'Monitor' : 
                       key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    
    return (
      <div key={key} className="space-y-4">
        <div className="flex items-center gap-2">
          <LucideBot className="h-4 w-4" />
          <Label className="text-base font-medium">{displayName} Configuration</Label>
        </div>
        
        <div className="flex flex-col md:flex-row gap-4">
          <div className="w-full md:w-64 flex-none space-y-2">
            <div className="flex items-center gap-2">
              <LucideBot className="h-4 w-4" />
              <Label>AI Model</Label>
            </div>
            <Select
              value={config.service_type}
              onValueChange={(val) => handlePromptConfigChange(key, { service_type: val as LLMServiceType })}
              disabled={isLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select AI model" />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(LLM_COSTS) as LLMServiceType[]).map((llmType) => (
                  <SelectItem key={llmType} value={llmType}>
                    <div className="flex flex-col">
                      <span>{LLM_DISPLAY_NAMES[llmType]}</span>
                      <span className="text-xs text-muted-foreground">{formatCost(llmType)}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex-grow space-y-2">
            <div className="flex items-center gap-2">
              <LucideFileText className="h-4 w-4" />
              <Label htmlFor={`${key}-prompt-textarea`} id={`${key}-prompt-label`}>Prompt</Label>
            </div>
            <textarea
              id={`${key}-prompt-textarea`}
              aria-labelledby={`${key}-prompt-label`}
              value={config.prompt_text || ''}
              onChange={(e) => handlePromptConfigChange(key, { prompt_text: e.target.value })}
              placeholder={`Enter ${displayName} prompt...`}
              className="min-h-[120px] w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2"
              disabled={isLoading}
            />
          </div>
        </div>
      </div>
    )
  }

  // Show loading state if configuration is still loading
  if (isLoading && Object.keys(promptConfig).length === 0) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Prompt Configuration</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">Loading configuration...</div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Prompt Configurations Card */}
      <Card>
        <CardHeader>
          <CardTitle>Prompt Configurations</CardTitle>
          <CardDescription>
            Configure prompts for different analysis types
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {Object.entries(promptConfig).map(([key, config]) => 
            renderPromptConfig(key, config)
          )}
        </CardContent>
      </Card>
    </div>
  )
}